import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { API_BASE } from './config';
import { createMonitorSocket } from '../socket/socket';
import { resolveAvatarSource } from '../utils/avatar';
import '../css/RankTable.css';


const RANKS = [
  'ALL',
  'BG',
  'BGM',
  'NB',
  'NB+',
  'N-',
  'N',
  'N/S',
  'S',
  'S/P'
];
const RANK_PAGE_SIZE = 20;
const getAvatarSource = avatarUrl => {
  return resolveAvatarSource(avatarUrl);
};

function RankTable() {

  /* =================================================
     NAVIGATE
  ================================================= */

  const navigate = useNavigate();

  const loggedInUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);

  const profileGroupId = Number(localStorage.getItem('last_group_id')) || 1;

  const openMonitor = () => {
    navigate('/monitor');
  };
  const openMyProfile = () => {
    if (!loggedInUser?.user_id) return;
    navigate(`/player-history/${profileGroupId}/${loggedInUser.user_id}`);
  };
const openPlayerHistory = player => {
  if (!player?.user_id) return;

  navigate(
    `/player-history/${profileGroupId}/${player.user_id}`
  );
};

  /* =================================================
     STATE
  ================================================= */

  const [players, setPlayers] = useState([]);

  const [rankFilter, setRankFilter] =
    useState('ALL');

  const [search, setSearch] =
    useState('');

  const [loading, setLoading] =
    useState(false);

  const [logoutBusy, setLogoutBusy] =
    useState(false);

  const [rankInfoOpen, setRankInfoOpen] =
    useState(false);

  const [showAllRanks, setShowAllRanks] =
    useState(true);

  const [rankPage, setRankPage] =
    useState(1);


  /* =================================================
     LOAD
  ================================================= */

  const loadRankTable = useCallback(async () => {

    setLoading(true);

    try {

      const params =
        new URLSearchParams();

      if (rankFilter !== 'ALL') {
        params.set(
          'rank',
          rankFilter
        );
      }

      const query =
        params.toString();

      const url =
        `${API_BASE}/api/game-match/rank-table` +
        `${query ? `?${query}` : ''}`;

      const res =
        await fetch(url);

      const data =
        await res.json();

      if (!res.ok) {
        throw new Error(
          data.message ||
          'Load rank failed'
        );
      }

      setPlayers(
        Array.isArray(data)
          ? data
          : []
      );

    } catch (err) {

      console.error(
        '[LOAD RANK ERROR]',
        err
      );

      setPlayers([]);

    } finally {

      setLoading(false);

    }
  }, [rankFilter]);


  useEffect(() => {
    loadRankTable();
  }, [loadRankTable]);

  const handleLogout = async () => {
    if (logoutBusy || !loggedInUser?.user_id) return;
    if (!window.confirm('ต้องการออกจากระบบใช่หรือไม่?\nสถานะของคุณในทุกก๊วนจะเปลี่ยนเป็นพักชั่วคราว')) return;

    try {
      setLogoutBusy(true);
      const token = localStorage.getItem('device_token') || localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/checkin/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'ออกจากระบบไม่สำเร็จ');
      localStorage.removeItem('token');
      localStorage.removeItem('device_token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('loginRedirect');
      navigate('/monitor');
    } catch (logoutError) {
      window.alert(logoutError.message || 'ออกจากระบบไม่สำเร็จ');
    } finally {
      setLogoutBusy(false);
    }
  };

  useEffect(() => {
    const realtimeSocket = createMonitorSocket();
    const refreshRank = () => loadRankTable();
    const events = [
      'rank-table-updated',
      'match-ended',
      'match-result-updated',
      'point-updated'
    ];

    events.forEach(eventName => realtimeSocket.on(eventName, refreshRank));

    return () => {
      events.forEach(eventName => realtimeSocket.off(eventName, refreshRank));
      realtimeSocket.disconnect();
    };
  }, [loadRankTable]);


  /* =================================================
     SEARCH
  ================================================= */

  const searchedPlayers =
    useMemo(() => {

      const keyword =
        search
          .trim()
          .toLowerCase();

      const searchedPlayers = keyword
        ? players.filter(
        player =>
          String(
            player.name || ''
          )
            .toLowerCase()
            .includes(keyword)
        )
        : [...players];

      return searchedPlayers;

    }, [players, search]);

  const totalRankPages = Math.max(1, Math.ceil(searchedPlayers.length / RANK_PAGE_SIZE));

  const filteredPlayers = useMemo(() => {
    if (!showAllRanks) return searchedPlayers.slice(0, 3);
    const start = (rankPage - 1) * RANK_PAGE_SIZE;
    return searchedPlayers.slice(start, start + RANK_PAGE_SIZE);
  }, [searchedPlayers, showAllRanks, rankPage]);

  useEffect(() => {
    setRankPage(1);
  }, [rankFilter, search, showAllRanks]);

  useEffect(() => {
    if (rankPage > totalRankPages) setRankPage(totalRankPages);
  }, [rankPage, totalRankPages]);


  /* =================================================
     MOVEMENT
  ================================================= */

  const renderMovement = player => {

    if (
      player.movement === 'UP'
    ) {
      return (
        <span className="movement-up">
          🔥 ▲ {player.movement_count}
        </span>
      );
    }

    if (
      player.movement === 'DOWN'
    ) {
      return (
        <span className="movement-down">
          ▼ {player.movement_count}
        </span>
      );
    }

    return (
      <span className="movement-same">
        ━
      </span>
    );
  };


  /* =================================================
     POINT CHANGE
  ================================================= */

  const renderPointChange = value => {

    const point =
      Number(value || 0);

    if (point > 0) {
      return (
        <span className="change-plus">
          +{point}
        </span>
      );
    }

    if (point < 0) {
      return (
        <span className="change-minus">
          {point}
        </span>
      );
    }

    return (
      <span className="change-zero">
        0
      </span>
    );
  };


  /* =================================================
     RENDER
  ================================================= */

  return (
    <div className="rank-page">

      {/* ================= HEADER ================= */}

      <div className="rank-header">

        <div>

          <h2>
            🏆 อันดับผู้เล่นก๊วนน้ามาด
          </h2>

          <div className="rank-subtitle">
            อันดับและคะแนนสะสม
          </div>

        </div>


        <div className="rank-header-actions">

          <div className="rank-player-count">
            👥 {showAllRanks
              ? `${searchedPlayers.length} คน`
              : `Top ${Math.min(3, searchedPlayers.length)} / ${searchedPlayers.length} คน`}
          </div>

          <button
            type="button"
            className="rank-monitor-button"
            onClick={openMonitor}
          >
            🏸 ข้อมูลสด
          </button>

          {loggedInUser?.user_id && (
            <button
              type="button"
              className="rank-my-profile-button"
              onClick={openMyProfile}
            >
              🔍 {loggedInUser.name || `รหัสผู้เล่น ${loggedInUser.user_id}`}
            </button>
          )}

        </div>

      </div>


      {/* ================= INFO ================= */}

      <div className="rank-info-card">

        <div className="rank-info-header">

          <span className="rank-info-icon">
            ℹ️
          </span>

          <div>

            <div className="rank-info-title">
              ระบบจัดอันดับ
            </div>

            <div className="rank-info-subtitle">
              คะแนนจะเปลี่ยนตามผลการแข่งขัน
            </div>

          </div>

          <button
            type="button"
            className="rank-info-toggle"
            aria-expanded={rankInfoOpen}
            aria-controls="rank-system-details"
            onClick={() => setRankInfoOpen(current => !current)}
          >
            {rankInfoOpen ? 'ซ่อน ▲' : 'แสดง ▼'}
          </button>

        </div>

        {rankInfoOpen && (
          <div id="rank-system-details" className="rank-info-details">
          <div className="rank-rule-list">

          {/* WIN */}

          <div className="rank-rule win">

            <span className="rule-icon">
              🏆
            </span>

            <div>
              <strong>
                ชนะ
              </strong>

              <span>
                คะแนนเพิ่ม
              </span>
            </div>

          </div>


          {/* LOSE */}

          <div className="rank-rule lose">

            <span className="rule-icon">
              ❌
            </span>

            <div>
              <strong>
                แพ้
              </strong>

              <span>
                คะแนนลด
              </span>
            </div>

          </div>


          {/* DRAW */}

          <div className="rank-rule draw">

            <span className="rule-icon">
              🤝
            </span>

            <div>
              <strong>
                เสมอ
              </strong>

              <span>
                คะแนนเท่าเดิม
              </span>
            </div>

          </div>

          </div>


          <div className="rank-auto-message">
            คะแนนใช้สำหรับจัดอันดับเท่านั้น ระดับมือจะไม่เพิ่มหรือลดอัตโนมัติ
          </div>
          </div>
        )}

      </div>


      {/* ================= FILTER ================= */}

      <div className="rank-filter-card">

        <div className="rank-filter-title">
          เลือกระดับมือ
        </div>


        <div className="rank-buttons">

          {RANKS.map(rank => (

            <button
              key={rank}
              type="button"
              data-rank={rank}
              className={
                rankFilter === rank
                  ? 'rank-filter-btn active'
                  : 'rank-filter-btn'
              }
              onClick={() =>
                setRankFilter(rank)
              }
            >

              {rank === 'ALL'
                ? 'ทั้งหมด'
                : rank}

            </button>

          ))}

        </div>

        <div className="rank-list-mode-row">
          <div className="rank-view-description top-points" role="status">
            {showAllRanks
              ? `📋 แสดงทุกอันดับ ${searchedPlayers.length} คน เรียงจากคะแนนสูงสุด`
              : '🏆 แสดงผู้เล่นคะแนนสูงสุดอันดับ 1–3 ระดับมือเป็นค่าที่ผู้ดูแลกำหนด'}
          </div>
          <button
            type="button"
            className={`rank-show-all-button ${showAllRanks ? 'is-active' : ''}`}
            onClick={() => setShowAllRanks(current => !current)}
          >
            {showAllRanks ? '🏆 แสดง 3 อันดับแรก' : `📋 ดูอันดับทั้งหมด (${searchedPlayers.length})`}
          </button>
        </div>

<div className="rank-extra-filter">

  <input
    type="text"
    className="rank-search"
    placeholder="🔎 ค้นหาชื่อผู้เล่น..."
    value={search}
    onChange={e =>
      setSearch(e.target.value)
    }
  />

  <div className="rank-profile-hint">
    <span className="rank-profile-hint-icon">
      👆
    </span>

    <span>
      คลิกที่
      <strong> รูปหรือชื่อผู้เล่น </strong>
      เพื่อดูโปรไฟล์ ประวัติการแข่งขัน และอัปเดตรูปโปรไฟล์
    </span>
  </div>

</div>

      </div>


      {/* ================= TABLE ================= */}

      <div className="rank-table-card">

        {loading ? (

          <div className="rank-loading">
            กำลังโหลด...
          </div>

        ) : (

          <div className="rank-table-scroll">

            <table className="rank-table">

              <thead>

                <tr>

                  <th>
                    อันดับ
                  </th>

                  <th>
                    ผู้เล่น
                  </th>

                  <th>
                    ระดับมือ
                  </th>

                  <th>
                    คะแนน
                  </th>

                  <th>
                    ล่าสุด
                  </th>

                  <th>
                    อันดับ
                  </th>

                </tr>

              </thead>


              <tbody>

                {filteredPlayers.map(
                  player => (

                    <tr
                      key={
                        player.user_id
                      }
                    >

                      {/* POSITION */}

                      <td className={`rank-number ${
                        player.movement === 'UP'
                          ? 'rank-number-up'
                          : player.movement === 'DOWN'
                            ? 'rank-number-down'
                            : 'rank-number-same'
                      }`}>

                        {player.position === 1
                          ? '🥇'
                          : player.position === 2
                          ? '🥈'
                          : player.position === 3
                          ? '🥉'
                          : `#${player.position}`}

                      </td>


                      {/* PLAYER */}

                    <td className="rank-player-name">

  <button
    type="button"
    className="rank-player-link"
    onClick={() =>
      openPlayerHistory(player)
    }
    title={`ดูประวัติของ ${player.name}`}
  >

    <span className="rank-player-avatar">

      {(player.avatarUrl ||
        player.avatar_url ||
        player.avatar) ? (

        <img
          src={getAvatarSource(
            player.avatarUrl ||
            player.avatar_url ||
            player.avatar
          )}
          alt={player.name || 'ผู้เล่น'}
          onError={event => {
            event.currentTarget.style.display =
              'none';

            const fallback =
              event.currentTarget.nextElementSibling;

            if (fallback) {
              fallback.style.display = 'grid';
            }
          }}
        />

      ) : null}

      <span
        className="rank-player-avatar-fallback"
        style={{
          display:
            (
              player.avatarUrl ||
              player.avatar_url ||
              player.avatar
            )
              ? 'none'
              : 'grid'
        }}
      >
        {player.name
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() || '🏸'}
      </span>

    </span>

    <span className="rank-player-name-text">
      {player.name}
    </span>

  </button>

</td>


                      {/* RANK */}

                      <td>

                        <span className="rank-badge">
                          {player.rank || '-'}
                        </span>

                      </td>


                      {/* POINT */}

                      <td>

                        <span className="rank-point">
                          {player.point ?? 0}
                        </span>

                      </td>


                      {/* POINT CHANGE */}

                      <td>

                        {renderPointChange(
                          player.latest_change
                        )}

                      </td>


                      {/* MOVEMENT */}

                      <td>

                        {renderMovement(
                          player
                        )}

                      </td>

                    </tr>

                  )
                )}


                {filteredPlayers.length === 0 && (

                  <tr>

                    <td
                      colSpan="6"
                      className="rank-empty"
                    >
                      ไม่พบผู้เล่น
                    </td>

                  </tr>

                )}

              </tbody>

            </table>

          </div>

        )}

      </div>

      {showAllRanks && totalRankPages > 1 && (
        <div className="rank-pagination" aria-label="เปลี่ยนหน้าตารางอันดับ">
          <button type="button" disabled={rankPage <= 1} onClick={() => setRankPage(page => Math.max(1, page - 1))}>← ก่อนหน้า</button>
          <span>หน้า <b>{rankPage}</b> / {totalRankPages} • ลำดับ {(rankPage - 1) * RANK_PAGE_SIZE + 1}–{Math.min(rankPage * RANK_PAGE_SIZE, searchedPlayers.length)}</span>
          <button type="button" disabled={rankPage >= totalRankPages} onClick={() => setRankPage(page => Math.min(totalRankPages, page + 1))}>ถัดไป →</button>
        </div>
      )}

      <nav className="rank-bottom-nav" aria-label="เมนูอันดับผู้เล่น">
        <button type="button" onClick={openMonitor}>
          <span>▣</span><small>ดูคิว</small>
        </button>
        <button type="button" className="is-current" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <span>🏆</span><small>อันดับ</small>
        </button>
        <button type="button" disabled={loading} onClick={loadRankTable}>
          <span>↻</span><small>{loading ? 'กำลังอัปเดต' : 'อัปเดต'}</small>
        </button>
        {loggedInUser?.user_id && (
          <button type="button" disabled={logoutBusy} onClick={handleLogout}>
            <span>⇥</span><small>{logoutBusy ? 'กำลังออก' : 'ออกจากระบบ'}</small>
          </button>
        )}
      </nav>

    </div>
  );
}


export default RankTable;
